import React, { useMemo, useRef, useState } from 'react';
import axios from '../axiosConfig';
import MathText from './MathText';
import { renderFigureSvg } from '../utils/assessmentFigures';
import { readQuestionImage } from '../utils/assessmentImages';
import { assessmentTranslator } from '../utils/assessmentLanguage';
import { normalizeAiError } from '../services/ai.js';
import './AssessmentPreview.css';

const OPTION_LETTERS = ['A', 'B', 'C', 'D'];

// The questions of a test or worksheet, each with a key that finds it again after edits.
export function previewQuestions(kind, assessment) {
    if (kind === 'worksheet') {
        return (assessment.questions || []).map((question, index) => ({ question, key: `q${index}`, sectionIndex: null, index }));
    }
    return (assessment.sections || []).flatMap((section, sectionIndex) => (section.questions || []).map((question, index) => ({ question, key: `s${sectionIndex}q${index}`, sectionIndex, index })));
}

function itemNumbers(kind, question) {
    return kind === 'worksheet'
        ? [String(question.questionNumber)]
        : (question.items || []).map((item) => String(item.itemNumber));
}

// Marks per cognitive level, recounted after a question changes. Targets stay as the server set them.
export function recountDistribution(kind, assessment) {
    const marks = {};
    for (const { question } of previewQuestions(kind, assessment)) {
        const entries = kind === 'worksheet' ? [question] : question.items || [];
        for (const entry of entries) marks[entry.cognitiveLevel] = (marks[entry.cognitiveLevel] || 0) + Number(entry.marks || 0);
    }
    const total = Object.values(marks).reduce((sum, value) => sum + value, 0);
    return (assessment.cognitiveDistribution || []).map((level) => ({
        ...level,
        marks: marks[level.level] || 0,
        actualPercentage: total ? Math.round(((marks[level.level] || 0) * 100) / total) : 0
    }));
}

// Swaps one question for its regenerated version, keeping the memo and marking grid in paper order.
export function replaceQuestion(kind, assessment, target, result) {
    const next = JSON.parse(JSON.stringify(assessment));
    const oldNumbers = itemNumbers(kind, target.question);
    const questionNumber = String(target.question.questionNumber);
    if (kind === 'worksheet') {
        next.questions[target.index] = result.question;
    } else {
        next.sections[target.sectionIndex].questions[target.index] = result.question;
    }

    const byItem = (entries, key) => Object.fromEntries((entries || []).map((entry) => [String(entry[key]), entry]));
    const memoKey = kind === 'worksheet' ? 'questionNumber' : 'itemNumber';
    const oldMemo = byItem((assessment.memorandum || []).filter((entry) => !oldNumbers.includes(String(entry[memoKey]))), memoKey);
    const newMemo = byItem(result.memorandum, memoKey);
    const oldGrid = byItem((assessment.markingGrid || []).filter((entry) => !oldNumbers.includes(String(entry.itemNumber))), 'itemNumber');
    const newGrid = byItem(result.markingGrid, 'itemNumber');
    const order = previewQuestions(kind, next).flatMap(({ question }) => itemNumbers(kind, question));
    next.memorandum = order.map((number) => newMemo[number] || oldMemo[number]).filter(Boolean);
    if (kind === 'test') next.markingGrid = order.map((number) => newGrid[number] || oldGrid[number]).filter(Boolean);

    next.cognitiveDistribution = recountDistribution(kind, next);
    // The balance warning describes the whole paper, so it is re-evaluated rather than kept.
    const offTarget = next.cognitiveDistribution.filter((level) => Math.abs((level.actualPercentage || 0) - (level.targetPercentage || 0)) > 15);
    const balance = next.balanceChecked && offTarget.length
        ? [{ questionNumber: '', kind: 'balance', message: `The cognitive levels are not balanced as CAPS requires: ${next.cognitiveDistribution.map((level) => `${level.level} ${level.actualPercentage}% (CAPS ${level.targetPercentage}%)`).join(', ')}. Regenerate or adjust some questions before use.` }]
        : [];
    next.reviewFlags = [
        ...(assessment.reviewFlags || []).filter((flag) => String(flag.questionNumber) !== questionNumber && flag.kind !== 'balance'),
        ...(result.reviewFlags || []),
        ...balance
    ];
    return next;
}

function FigureView({ figure, notToScale }) {
    const rendered = useMemo(() => renderFigureSvg(figure, { notToScale }), [figure, notToScale]);
    if (!rendered) return null;
    return <figure className="assessment-preview__figure">
        {/* The SVG is generated by renderFigureSvg, which escapes every piece of text. */}
        <div style={{ maxWidth: rendered.width }} dangerouslySetInnerHTML={{ __html: rendered.svg }} />
        {figure.caption && <figcaption><MathText>{figure.caption}</MathText></figcaption>}
    </figure>;
}

function TableView({ table }) {
    if (!table?.headers?.length) return null;
    return <div className="assessment-preview__table">
        {table.caption && <strong><MathText>{table.caption}</MathText></strong>}
        <table>
            <thead><tr>{table.headers.map((header, index) => <th key={index}><MathText>{header}</MathText></th>)}</tr></thead>
            <tbody>{table.rows.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}><MathText>{cell}</MathText></td>)}</tr>)}</tbody>
        </table>
    </div>;
}

function FlagList({ flags }) {
    if (!flags.length) return null;
    return <ul className="assessment-preview__flags">{flags.map((flag, index) => <li key={index} className={`assessment-preview__flag assessment-preview__flag--${flag.kind}`}>{flag.message}</li>)}</ul>;
}

export default function AssessmentPreview({ kind, assessment, onChange, form }) {
    const [busyKey, setBusyKey] = useState('');
    const [openKey, setOpenKey] = useState('');
    const [editKey, setEditKey] = useState('');
    const [instruction, setInstruction] = useState('');
    const [showMemo, setShowMemo] = useState(false);
    const [error, setError] = useState('');
    const fileInputs = useRef({});
    const translate = assessmentTranslator(assessment.outputLanguage || form.outputLanguage || 'English');
    const notToScale = translate('Not drawn to scale');
    const questions = previewQuestions(kind, assessment);
    const flags = assessment.reviewFlags || [];
    const generalFlags = flags.filter((flag) => !flag.questionNumber);
    const memoKey = kind === 'worksheet' ? 'questionNumber' : 'itemNumber';

    const withQuestion = (target, changes) => {
        const next = JSON.parse(JSON.stringify(assessment));
        const list = kind === 'worksheet' ? next.questions : next.sections[target.sectionIndex].questions;
        list[target.index] = JSON.parse(JSON.stringify({ ...list[target.index], ...changes }));
        return next;
    };
    const updateQuestion = (target, changes) => onChange(withQuestion(target, changes));

    const regenerate = async (target) => {
        setBusyKey(target.key);
        setError('');
        try {
            const section = kind === 'test' ? assessment.sections[target.sectionIndex] : null;
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/ai/regenerate-question.php`, {
                kind,
                subject: form.subject,
                grade: form.grade,
                term: form.term,
                topic: form.topic,
                difficulty: form.difficulty,
                outputLanguage: form.outputLanguage,
                isLanguage: form.isLanguage || assessment.isLanguage,
                question: target.question,
                teacherInstruction: instruction,
                sectionIntroText: section?.introText || '',
                otherQuestionTitles: questions.filter(({ key }) => key !== target.key).map(({ question }) => question.title || question.questionText)
            }, { timeout: 240000 });
            onChange(replaceQuestion(kind, assessment, target, response.data));
            setOpenKey('');
            setInstruction('');
        } catch (requestError) {
            setError(normalizeAiError(requestError));
        } finally {
            setBusyKey('');
        }
    };

    const attachImage = async (target, file) => {
        setError('');
        try {
            const image = await readQuestionImage(file);
            const next = withQuestion(target, { image });
            // A picture answers "add an image" and "missing figure" warnings for this question.
            next.reviewFlags = (next.reviewFlags || []).filter((flag) => !(String(flag.questionNumber) === String(target.question.questionNumber) && flag.kind === 'figure'));
            onChange(next);
        } catch (imageError) {
            setError(imageError.message);
        }
    };

    const editField = (target, path, value) => {
        const question = JSON.parse(JSON.stringify(target.question));
        if (path.length === 1) question[path[0]] = value;
        else question[path[0]][path[1]][path[2]] = value;
        updateQuestion(target, question);
    };

    return <section className="assessment-preview">
        <div className="assessment-preview__summary">
            <div>
                <strong>{flags.length ? `${flags.length} thing${flags.length === 1 ? '' : 's'} to check before printing` : 'No problems found'}</strong>
                <span>
                    {assessment.memoVerified === true && 'The memorandum was checked independently. '}
                    {assessment.cognitiveFrameworkVerified === false && 'This subject has no fixed CAPS weighting in Marksure, so confirm the cognitive levels against your ATP. '}
                    Review every question before use.
                </span>
            </div>
            <label className="assessment-preview__memo-toggle"><input type="checkbox" checked={showMemo} onChange={(event) => setShowMemo(event.target.checked)} /> Show memorandum</label>
        </div>
        <FlagList flags={generalFlags} />
        {error && <div className="assessment-preview__error">{error}</div>}

        <div className="assessment-preview__paper">
            {questions.map((target) => {
                const { question, key, sectionIndex, index } = target;
                const section = kind === 'test' ? assessment.sections[sectionIndex] : null;
                const questionFlags = flags.filter((flag) => String(flag.questionNumber) === String(question.questionNumber));
                const memo = (assessment.memorandum || []).filter((entry) => itemNumbers(kind, question).includes(String(entry[memoKey])));
                const editing = editKey === key;
                const busy = busyKey === key;
                return <React.Fragment key={key}>
                    {section && index === 0 && <header className="assessment-preview__section">
                        <h3>{section.title} <span>[{section.marks}]</span></h3>
                        {section.introText && <div className="assessment-preview__intro"><MathText>{section.introText}</MathText></div>}
                    </header>}
                    <article className={`assessment-preview__question${questionFlags.length ? ' has-flags' : ''}${busy ? ' is-busy' : ''}`}>
                        <div className="assessment-preview__question-head">
                            <b>{question.questionNumber}</b>
                            {editing
                                ? <textarea value={kind === 'test' ? question.title : question.questionText} onChange={(event) => editField(target, [kind === 'test' ? 'title' : 'questionText'], event.target.value)} rows={2} />
                                : <span><MathText>{kind === 'test' ? question.title : question.questionText}</MathText></span>}
                            <em>({question.marks})</em>
                        </div>
                        <FlagList flags={questionFlags} />
                        {question.figure && <FigureView figure={question.figure} notToScale={notToScale} />}
                        <TableView table={question.table} />
                        {question.image && <figure className="assessment-preview__figure"><img src={question.image.dataUrl} alt={question.image.name || 'Question picture'} /></figure>}

                        {kind === 'test' && (question.items || []).map((item, itemIndex) => <div key={item.itemNumber} className="assessment-preview__item">
                            <b>{item.itemNumber}</b>
                            {editing
                                ? <textarea value={item.prompt} onChange={(event) => editField(target, ['items', itemIndex, 'prompt'], event.target.value)} rows={2} />
                                : <span><MathText>{item.prompt}</MathText>{(item.options || []).length > 0 && <ol className="assessment-preview__options">{item.options.map((option, optionIndex) => <li key={optionIndex}><b>{OPTION_LETTERS[optionIndex]}</b> <MathText>{option}</MathText></li>)}</ol>}</span>}
                            <em>({item.marks})</em>
                        </div>)}
                        {kind === 'worksheet' && (question.subQuestions || []).map((sub, subIndex) => <div key={sub.label || subIndex} className="assessment-preview__item">
                            <b>{sub.label}</b>
                            {editing
                                ? <textarea value={sub.text} onChange={(event) => editField(target, ['subQuestions', subIndex, 'text'], event.target.value)} rows={2} />
                                : <span><MathText>{sub.text}</MathText></span>}
                            <em>({sub.marks})</em>
                        </div>)}

                        {showMemo && memo.length > 0 && <div className="assessment-preview__memo">
                            {memo.map((entry) => <div key={entry[memoKey]}>
                                <b>{entry[memoKey]}</b>
                                {kind === 'test'
                                    ? <span><MathText>{entry.solution}</MathText>{entry.markingGuideline && <small><MathText>{entry.markingGuideline}</MathText></small>}</span>
                                    : <span>{(entry.solutionSteps || []).map((step, stepIndex) => <span key={stepIndex} className="assessment-preview__memo-step"><MathText>{step}</MathText></span>)}<small>{translate('Final answer')}: <MathText>{entry.finalAnswer}</MathText></small></span>}
                            </div>)}
                        </div>}

                        <div className="assessment-preview__actions">
                            <button type="button" onClick={() => setOpenKey(openKey === key ? '' : key)} disabled={Boolean(busyKey)}>{busy ? 'Regenerating...' : 'Regenerate'}</button>
                            <button type="button" onClick={() => fileInputs.current[key]?.click()} disabled={Boolean(busyKey)}>{question.image ? 'Replace picture' : 'Add picture'}</button>
                            {question.image && <button type="button" onClick={() => updateQuestion(target, { image: undefined })}>Remove picture</button>}
                            {question.figure && <button type="button" onClick={() => updateQuestion(target, { figure: undefined })}>Remove diagram</button>}
                            <button type="button" onClick={() => setEditKey(editing ? '' : key)} disabled={Boolean(busyKey)}>{editing ? 'Done editing' : 'Edit wording'}</button>
                            <input type="file" accept="image/*" hidden ref={(element) => { fileInputs.current[key] = element; }} onChange={(event) => { attachImage(target, event.target.files?.[0]); event.target.value = ''; }} />
                        </div>
                        {openKey === key && <div className="assessment-preview__regenerate">
                            <input value={instruction} onChange={(event) => setInstruction(event.target.value)} placeholder="Optional: what should change? e.g. use a bar graph, make it easier, use rand amounts" maxLength={600} />
                            <button type="button" onClick={() => regenerate(target)} disabled={busy}>{busy ? 'Working...' : `Replace question ${question.questionNumber}`}</button>
                        </div>}
                    </article>
                </React.Fragment>;
            })}
        </div>

        {(assessment.cognitiveDistribution || []).length > 0 && <div className="assessment-preview__distribution">
            <strong>{translate(assessment.cognitiveFramework || 'Cognitive level')}</strong>
            {assessment.cognitiveDistribution.map((level) => <div key={level.level}>
                <span>{translate(level.level)}</span>
                <span>{level.targetPercentage ? `target ${level.targetPercentage}%` : ''}</span>
                <b>{level.marks} marks ({level.actualPercentage ?? 0}%)</b>
            </div>)}
        </div>}
    </section>;
}
